import Confetti from '@theme/components/Confetti/src/jsx/Confetti';
import { useEffect } from 'react';
import Button, { DangerButton, PrimaryButton } from '../../../../../Button/src/tsx/Button';
import { useBookingContext } from '../../contexts/BookingContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useICSExport } from '../../hooks/useICSExport';
import { formatFullDate } from '../../utils/dates';
import Heading from '../atoms/Heading';
import AppointmentInfo from '../molecules/AppointmentInfo';
import Host from '../molecules/Host';
import Container, { Wrapper } from '../organisms/Container';
import Navigation from '../organisms/Navigation';

interface AppointmentConfirmationProps {
	getLabel: (key: string) => string;
}

export default function AppointmentConfirmation({ getLabel }: AppointmentConfirmationProps) {
	const { appointment, completeTask, locale, timezone, service, setScene } = useBookingContext();

	useEffect(() => {
		completeTask();
		setScene('appointment_confirmation');
	}, []);

	const { gtmEventDownloadICS } = useAnalytics(service.businessId, service.serviceId);
	const { handleICSDownload } = useICSExport({
		appointmentData: appointment.data,
		getLabel,
		gtmEventDownloadICS,
	});

	if (!appointment.data) {
		return null;
	}

	const { startDatetime, endDatetime, hostName, hostImageUrl, manageBookingUrl } = appointment.data;
	const timeSlotString = formatFullDate(startDatetime, endDatetime, locale, timezone.current);

	return (
		<Container id="appointment-confirmation">
			<Confetti />
			<Wrapper>
				<Heading title={getLabel('booking_completed_heading')} />
				<AppointmentInfo heading={getLabel('appointment_info_preferred')} timeSlotString={timeSlotString}>
					<Host appointmentStartDateTime={startDatetime} getLabel={getLabel} hostImageUrl={hostImageUrl} hostName={hostName} />
				</AppointmentInfo>
			</Wrapper>
			<Navigation keyProp="appointment-confirmation">
				<PrimaryButton onClick={handleICSDownload} testId="download-appointment-ics" specifier="file">
					{getLabel('booking_button_download_appointment')}
				</PrimaryButton>

				{manageBookingUrl && (
					<Button href={manageBookingUrl} testId="manage-appointment" blank={true}>
						{getLabel('booking_button_manage_appointment')}
					</Button>
				)}

				<DangerButton onClick={appointment.cancel} testId="cancel-appointment">
					{getLabel('booking_button_cancel_appointment')}
				</DangerButton>
			</Navigation>
		</Container>
	);
}
